import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PendingReview() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [idSkipped, setIdSkipped] = useState(false);
  const [drivingLicence, setDrivingLicence] = useState(null);
  const [passport, setPassport] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    const check = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/agreements/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setIdSkipped(data.id_upload_skipped === true);
      } catch (err) {
        console.error(err);
      }
    };
    check();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleUpload = async () => {
    if (!drivingLicence && !passport) return setUploadError('Please select at least one document (driving licence or passport)');
    setUploading(true);
    setUploadError('');
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      if (drivingLicence) fd.append('driving_licence', drivingLicence);
      if (passport)       fd.append('passport', passport);

      const res = await fetch('/api/agreements/upload-id', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setUploadDone(true);
      setIdSkipped(false);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>BrokerConnect</div>

        <div style={styles.iconWrap}>
          <span style={styles.icon}>⏳</span>
        </div>

        <h2 style={styles.title}>Application Under Review</h2>
        <p style={styles.sub}>Hi {user.name || 'there'},</p>
        <p style={styles.body}>
          Thank you for completing your introducer agreement. Your application is currently being reviewed by your broker.
        </p>
        <p style={styles.body}>
          You will receive an email notification once your application has been approved and your account is activated.
          Most applications are reviewed within <strong>1 business day</strong>.
        </p>

        {/* ID Upload Section */}
        {idSkipped && !uploadDone && (
          <div style={styles.idBox}>
            <p style={styles.idTitle}>⚠️ ID Documents Required</p>
            <p style={styles.idSub}>You skipped uploading your ID. Please upload your driving licence or passport (either is fine) now to avoid delays in your application being approved.</p>

            <label style={styles.label}>Driving Licence (JPG, PNG or PDF)</label>
            <input type="file" accept=".jpg,.jpeg,.png,.pdf"
              onChange={e => setDrivingLicence(e.target.files[0])}
              style={styles.fileInput} />
            {drivingLicence && <p style={styles.fileOk}>✓ {drivingLicence.name}</p>}

            <label style={styles.label}>Passport (JPG, PNG or PDF)</label>
            <input type="file" accept=".jpg,.jpeg,.png,.pdf"
              onChange={e => setPassport(e.target.files[0])}
              style={styles.fileInput} />
            {passport && <p style={styles.fileOk}>✓ {passport.name}</p>}

            {uploadError && <p style={styles.error}>{uploadError}</p>}

            <button onClick={handleUpload} disabled={uploading} style={styles.uploadBtn}>
              {uploading ? 'Uploading...' : 'Upload ID Documents'}
            </button>
          </div>
        )}

        {uploadDone && (
          <div style={styles.successBox}>
            ✅ ID documents uploaded successfully. Your broker has been notified.
          </div>
        )}

        <div style={styles.infoBox}>
          <p style={styles.infoTitle}>What happens next?</p>
          <ul style={styles.list}>
            <li>Your broker will review your agreement and ID documents</li>
            <li>You'll receive an email when approved</li>
            <li>Once approved, you'll have full access to submit leads</li>
          </ul>
        </div>

        <button onClick={handleLogout} style={styles.logoutBtn}>Sign Out</button>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { background: '#fff', borderRadius: 16, padding: 48, width: 520, maxWidth: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', textAlign: 'center' },
  logo: { fontSize: 22, fontWeight: 700, color: '#10b981', marginBottom: 32 },
  iconWrap: { width: 80, height: 80, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' },
  icon: { fontSize: 36 },
  title: { fontSize: 24, fontWeight: 700, color: '#0e1b2c', margin: '0 0 8px' },
  sub: { fontSize: 16, color: '#4b5a6e', margin: '0 0 12px' },
  body: { fontSize: 14, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.6 },
  idBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '20px', margin: '20px 0', textAlign: 'left' },
  idTitle: { fontSize: 15, fontWeight: 700, color: '#92400e', margin: '0 0 8px' },
  idSub: { fontSize: 13, color: '#78350f', margin: '0 0 16px', lineHeight: 1.5 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 },
  fileInput: { width: '100%', marginBottom: 6, fontSize: 13 },
  fileOk: { fontSize: 12, color: '#10b981', margin: '0 0 12px' },
  error: { fontSize: 13, color: '#dc2626', margin: '0 0 10px' },
  uploadBtn: { width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
  successBox: { background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#065f46', margin: '16px 0', textAlign: 'left' },
  infoBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '16px 20px', margin: '24px 0', textAlign: 'left' },
  infoTitle: { fontSize: 14, fontWeight: 600, color: '#065f46', margin: '0 0 10px' },
  list: { margin: 0, paddingLeft: 20, fontSize: 13, color: '#374151', lineHeight: 2 },
  logoutBtn: { padding: '10px 24px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 14, color: '#6b7280', marginTop: 8 }
};
